import React, { useMemo, useRef, useEffect, useState } from 'react';
import { AgCharts } from 'ag-charts-react';
import { CHART_COLORS, getAgChartTheme, getChartColors, formatPercent, formatNumber } from './chartTheme';
import { useTheme } from '../../../stores/ThemeContext';
/**
 * @typedef {Object} Trade
 * @property {number} profit - Trade profit/loss
 * @property {string} [trade_date] - Trade date (ISO string)
 * @property {string} [closeTime] - Close time (ISO string)
 */

/**
 * @typedef {Object} RollingDataPoint
 * @property {number} index - Trade index (1-based)
 * @property {Date} date - Trade date
 * @property {string} dateLabel - Formatted date label
 * @property {number} winRate - Rolling win rate (0-100)
 * @property {number} windowWins - Wins in the window
 * @property {number} windowSize - Actual window size
 */

/**
 * @typedef {'index' | 'date'} XAxisMode
 */

/**
 * Breakeven threshold for categorizing trades
 */
const BREAKEVEN_THRESHOLD = 0.01;

/**
 * Rolling Win Rate Line Chart
 * Computes and displays rolling win rate over time or trade sequence
 *
 * @param {Object} props
 * @param {Trade[]} props.trades - Array of trade objects with profit and date
 * @param {number} [props.window=20] - Rolling window size (last N trades)
 * @param {XAxisMode} [props.xAxis='index'] - X-axis mode: 'index' or 'date'
 * @param {string} [props.title='Rolling Win Rate'] - Chart title
 * @param {number} [props.height=300] - Chart height in pixels
 * @param {string} [props.className=''] - Additional CSS classes
 *
 * @example
 * // Default: Rolling win rate by trade index
 * <RollingWinRateChart trades={trades} />
 *
 * @example
 * // With custom window size and date x-axis
 * <RollingWinRateChart
 *   trades={trades}
 *   window={10}
 *   xAxis="date"
 *   title="10-Trade Rolling Win Rate"
 *   height={400}
 * />
 */
const RollingWinRateChart = ({
  trades = [],
  window: windowSize = 20,
  xAxis = 'index',
  title = 'Rolling Win Rate',
  height = 300,
  className = '',
}) => {
  const { isDark } = useTheme();
  const chartColors = getChartColors(isDark);
  const chartRef = useRef(null);
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height });

  // Handle responsive resizing
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width } = entry.contentRect;
        if (width > 0) {
          setDimensions({ width, height });
        }
      }
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [height]);

  // Compute rolling win rate data
  const chartData = useMemo(() => {
    if (!trades || trades.length === 0) {
      return [];
    }

    // Sort trades by date
    const sortedTrades = [...trades].sort((a, b) => {
      const dateA = new Date(a.trade_date || a.closeTime || 0);
      const dateB = new Date(b.trade_date || b.closeTime || 0);
      return dateA - dateB;
    });

    // Create array to track wins (1) and losses (0)
    const results = sortedTrades.map((trade) => {
      const profit = Number(trade.profit) || 0;
      return profit > BREAKEVEN_THRESHOLD ? 1 : 0;
    });

    /** @type {RollingDataPoint[]} */
    const data = [];

    // Calculate rolling win rate for each point
    for (let i = 0; i < sortedTrades.length; i++) {
      // Get window of results (up to windowSize trades ending at current index)
      const windowStart = Math.max(0, i - windowSize + 1);
      const windowResults = results.slice(windowStart, i + 1);
      const actualWindowSize = windowResults.length;
      const windowWins = windowResults.reduce((sum, r) => sum + r, 0);
      const winRate = (windowWins / actualWindowSize) * 100;

      const tradeDate = new Date(sortedTrades[i].trade_date || sortedTrades[i].closeTime || new Date());

      data.push({
        index: i + 1,
        date: tradeDate,
        dateLabel: tradeDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        winRate: Math.round(winRate * 10) / 10,
        windowWins,
        windowSize: actualWindowSize,
        refLine: 50, // Reference line at 50%
      });
    }

    return data;
  }, [trades, windowSize]);

  // Calculate average win rate for reference line
  const avgWinRate = useMemo(() => {
    if (chartData.length === 0) return 0;
    const sum = chartData.reduce((acc, d) => acc + d.winRate, 0);
    return sum / chartData.length;
  }, [chartData]);

  // Dynamic title with window size
  const chartTitle = title === 'Rolling Win Rate'
    ? `Rolling Win Rate (${windowSize}-Trade Window)`
    : title;

  // Chart options
  const options = useMemo(() => {
    const xKey = xAxis === 'date' ? 'date' : 'index';
    const xType = xAxis === 'date' ? 'time' : 'number';

    return {
      theme: getAgChartTheme(isDark),
      data: chartData,
      title: {
        text: chartTitle,
        fontSize: 16,
        fontWeight: 600,
        color: chartColors.text,
      },
      series: [
        {
          type: 'line',
          xKey,
          yKey: 'winRate',
          yName: 'Win Rate',
          stroke: CHART_COLORS.primary,
          strokeWidth: 2,
          marker: {
            enabled: chartData.length <= 50,
            fill: CHART_COLORS.primary,
            stroke: CHART_COLORS.primary,
            size: 4,
          },
          interpolation: { type: 'smooth' },
          tooltip: {
            renderer: ({ datum }) => ({
              title: xAxis === 'date'
                ? `Trade #${datum.index} (${datum.dateLabel})`
                : `Trade #${datum.index}`,
              content: `Win Rate: ${formatPercent(datum.winRate)}\nWins in Window: ${datum.windowWins}/${datum.windowSize}`,
            }),
          },
          highlightStyle: {
            item: {
              fill: CHART_COLORS.primary,
              strokeWidth: 3,
            },
          },
        },
        // 50% reference line - uses refLine field added to data
        {
          type: 'line',
          xKey,
          yKey: 'refLine',
          yName: '50% Line',
          stroke: CHART_COLORS.neutral,
          strokeWidth: 1,
          lineDash: [4, 4],
          marker: { enabled: false },
          showInLegend: false,
        },
      ],
      axes: [
        {
          type: xType,
          position: 'bottom',
          title: {
            text: xAxis === 'date' ? 'Date' : 'Trade Number',
            color: chartColors.textMuted,
            fontSize: 12,
          },
          label: {
            color: chartColors.textMuted,
            fontSize: 11,
            ...(xAxis === 'date' && {
              format: '%b %d',
            }),
          },
          line: {
            stroke: CHART_COLORS.axis,
          },
          gridLine: {
            style: [{
              stroke: CHART_COLORS.gridLine,
              lineDash: [4, 2],
            }],
          },
        },
        {
          type: 'number',
          position: 'left',
          title: {
            text: 'Win Rate (%)',
            color: chartColors.textMuted,
            fontSize: 12,
          },
          min: 0,
          max: 100,
          label: {
            color: chartColors.textMuted,
            fontSize: 11,
            formatter: ({ value }) => `${value}%`,
          },
          line: {
            stroke: CHART_COLORS.axis,
          },
          gridLine: {
            style: [{
              stroke: CHART_COLORS.gridLine,
              lineDash: [4, 2],
            }],
          },
        },
      ],
      legend: {
        enabled: false,
      },
      background: {
        fill: CHART_COLORS.background,
      },
      // Add crosshairs for better data reading
      tooltip: {
        enabled: true,
      },
    };
  }, [chartData, chartTitle, xAxis, isDark]);

  // Empty state
  if (!trades || trades.length === 0) {
    return (
      <div
        ref={containerRef}
        className={`flex items-center justify-center ${className}`}
        style={{ height }}
      >
        <div className="text-center text-muted-foreground">
          <p className="text-lg font-medium">No trade data</p>
          <p className="text-sm">Add trades to see rolling win rate</p>
        </div>
      </div>
    );
  }

  // Insufficient data warning
  if (trades.length < windowSize) {
    return (
      <div ref={containerRef} className={`w-full ${className}`} style={{ height }}>
        <AgCharts ref={chartRef} options={options} />
        <p className="text-xs text-muted-foreground text-center mt-2">
          * Showing partial window data. Need {windowSize} trades for full rolling window.
        </p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`w-full ${className}`} style={{ height }}>
      <AgCharts ref={chartRef} options={options} />
    </div>
  );
};

export default RollingWinRateChart;
